import React, { useContext, useEffect, useState } from "react";
import Tooltip from "@mui/material/Tooltip";
import styled from "styled-components";
import {
    FlexContainer,
    FormField,
    StandardModalHelperText,
    StandardModalInfoCard,
    Text,
    ToggleSwitch,
    portalColors
} from "src/components";
import UserAvatar from "../UserAvatar";

import { ChatContext } from "src/scenes/Chat/ChatContext";
import { FORM_TYPE_EDIT, USER_TYPE_EMPLOYEES, USER_TYPE_CLIENTS, FORM_TYPE_CREATE } from "src/constants/chat";
import { SearchInput } from "../CreateChatModal";
import { capitalizeString, trimStringWithEllipsis, isUserOnline, customNames } from "src/utils/helpers";
import pluralize from "pluralize";

import PlusIcon from "src/img/new/chats/add.svg";
import RemoveIcon from "src/img/new/chats/close.svg";

const Container = styled.div`
    position: relative;
    width: 90%;
    display: flex;
    flex-direction: column;
    margin: ${p => p.margin || "0"}
`;

export const Card = styled.div`
    position: relative;
    background: ${portalColors.surface};
    border: 1px solid ${portalColors.border};
    border-radius: 0.5rem;
    height: clamp(14rem, 38vh, 20rem);
    width: 100%;
    box-sizing: border-box;
    overflow-y: auto;
    padding: 0.525rem;

    ::-webkit-scrollbar {
        width: 0.375rem;
        height: 0.5rem;
        border-radius: 0.375rem;
        border-right: none;
        border-left: none;
    }

    ::-webkit-scrollbar-thumb {
        background-color: var(--calendar-border-strong, #DBE5ED);
        border-radius: 0.375rem;
    }

    ::-webkit-scrollbar-track {
        margin-left: 1.875rem;
    }

    ::-webkit-scrollbar-track-piece:start {
        border-radius: 0.375rem;
    }
`;

export const StyledInput = styled(FormField)`
    position: relative;
    background-repeat: no-repeat;
    background-position: calc(100% - 0.9375rem) center;
    border: 1px solid ${portalColors.border};
    background: ${portalColors.surface};
    color: ${portalColors.text};
    width: 100%;
    max-width: 53.9375rem;
    height: 2.5rem;
    font-size: 0.875rem;
    text-align: left;
    padding: 0 0.9375rem 0 0.9375rem;
    border-radius: 0.5rem !important;
    margin: 0 0 1.5rem 0;

    ::placeholder {
        text-align: left;
        color: ${portalColors.textSoft};
        font-size: 0.875rem;
    }
`;

const Upper = styled.div`
    margin: 0 0 0.875rem 0;
    height: 4.6875rem;
    width: 100%;
`;

const ListItem = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1rem 0.4375rem;
    box-sizing: border-box;
    border-bottom: ${p => p.borderBottom};
`;

const AddOrRemoveIcon = styled.img`
    height: 1.8125rem;
    width: 1.8125rem;
    cursor: pointer;
    margin-right: 1.3125rem;
`;

const MemberActions = styled.div`
    display: flex;
    align-items: center;
    gap: 0.75rem;
`;

const AdminRadioTooltipTarget = styled.span`
    display: inline-flex;
`;

const AdminRadioButton = styled.button`
    width: 1.5rem;
    height: 1.5rem;
    display: grid;
    place-items: center;
    flex: 0 0 auto;
    padding: 0;
    border: 2px solid ${p => p.$selected ? portalColors.blue : portalColors.borderStrong};
    border-radius: 50%;
    background: ${portalColors.surface};
    cursor: ${p => p.disabled ? "not-allowed" : "pointer"};
    opacity: ${p => p.disabled ? 0.45 : 1};

    &::after {
        width: 0.75rem;
        height: 0.75rem;
        border-radius: 50%;
        background: ${portalColors.blue};
        content: "";
        opacity: ${p => p.$selected ? 1 : 0};
    }

    &:focus-visible {
        outline: 3px solid ${portalColors.blueSoft};
        outline-offset: 2px;
    }
`;

const PendingRemovalBadge = styled.span`
    display: inline-flex;
    align-items: center;
    min-height: 1.75rem;
    padding: 0 0.65rem;
    border: 1px solid ${portalColors.danger};
    border-radius: 999px;
    background: rgba(214, 69, 69, 0.1);
    color: ${portalColors.danger};
    font-size: 0.75rem;
    font-weight: 800;
    white-space: nowrap;
`;

const SettingRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;

    @media (max-width: 640px) {
        align-items: flex-start;
        flex-direction: column;
    }
`;

const FlexRow = styled.div`
    display: flex;
    justify-content: space-between;
    margin-bottom: 2rem;
    gap: 1rem;

    @media (max-width: 760px) {
        flex-direction: column;
    }
`;

const Group = (props) => {
    const { 
        selectedUserListType, availableUsersList, searchFilteredUserList, handleSearch,
        handleSetGroupChatName, formType, hasLeadershipRoles
    } = props;

    const {
        matrixClient,
        currentMatrixUserId,
        useChatMessage: ctxUseChatMessage
    } = useContext(ChatContext);

    const {
        openedMatrixChat,
        displayOtherUserProfileModal,
    } = ctxUseChatMessage;
    
    const getUserId = (user) => user?.employeeUuid || user?.uuid;

    const allUsersMap = new Map();
    [...(availableUsersList || []), ...(searchFilteredUserList || [])].forEach(user => {
        allUsersMap.set(getUserId(user), user);
    });
    
    const [chatName, setChatName] = useState(formType === FORM_TYPE_EDIT && openedMatrixChat ? openedMatrixChat.name : "");
    const isEmployee = selectedUserListType === USER_TYPE_EMPLOYEES;
    const pendingRemovalUuids = new Set(
        openedMatrixChat?.users?.filter(user => user.currentMember && user.requestingLeave)
            .map(user => user.employeeUuid) || []
    );
    const eligibleNewAdminUuids = new Set(
        openedMatrixChat?.users?.filter(user => user.currentMember && user.couldOwnThisChat)
            .map(user => user.employeeUuid) || []
    );

    useEffect(() => handleSetGroupChatName(chatName), [chatName]);

    const getIsClient = (user) => {
        if (user.employeeDetails) return user.employeeDetails.isClient;
        return user.isClient;
    };

    const handleUserSelection = (uuid, userType) => {
        props.setFormData(prev => {
            const currentList = prev.selectedUsersByType[userType] || [];

            const updatedList = currentList.includes(uuid)
                ? currentList.filter(u => u !== uuid)
                : [...currentList, uuid];
                
            return {
                ...prev,
                selectedUsersByType: {
                    ...prev.selectedUsersByType,
                    [userType]: updatedList
                }
            };
        });
    };

    const noUsersToDisplay = () => (
        <div style={{ padding: "15px" }}>No users to display.</div>
    );

    const renderUnselectedUsersList = () => {
        const availableUsers = (searchFilteredUserList || []).filter(user => {
            const matchesType =
                selectedUserListType === USER_TYPE_CLIENTS
                    ? getIsClient(user)
                    : !getIsClient(user);

            const isNotSelected =
                !props.formData.selectedUsersByType[selectedUserListType]?.includes(getUserId(user));

            const isNotCurrentUser =
                user.matrixUserId !== currentMatrixUserId;

            return matchesType && isNotSelected && isNotCurrentUser;
        });

        if (availableUsers.length === 0) return noUsersToDisplay();

        return availableUsers.map((user, index) => {
            const userFullName = trimStringWithEllipsis(capitalizeString(user.firstName) + " " + capitalizeString(user.lastName));
            const isSelected =
                props.formData.selectedUsersByType[selectedUserListType]?.includes(getUserId(user));
            return (
                <ListItem key={getUserId(user)}
                    borderBottom={index + 1 === availableUsers.length ? "none" : "1px solid #DBE5ED"}
                >
                    <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
                        <UserAvatar
                            width="3.125rem"
                            height="3.125rem"
                            hideInfo={true}
                            photo={user.profilePictureUrl}
                            background="#D6D6D6"
                            firstName={user.firstName}
                            lastName={user.lastName}
                            statusBorder="1px solid #fff"
                            hideStatus={true}
                            withShadow={true}
                            pixelBorder="1px"
                            showPresenceStatus={true}
                            isOnline={isUserOnline(matrixClient, user.matrixUserId)}
                            onClickUserAvatarFunction={() => displayOtherUserProfileModal(user)}
                        />
                        <Text margin="0" color="#7B8793" size="1rem" weight="500">
                            {userFullName}
                        </Text>
                    </div>
                    {!isSelected && !props.isSelectingNewAdmin && (
                        <AddOrRemoveIcon
                            src={PlusIcon}
                            alt="add"
                            onClick={() => handleUserSelection(getUserId(user), selectedUserListType)}
                        />
                    )}
                </ListItem>
            );
        });
    };

    const renderSelectedUsersList = () => {
        const currentUser = availableUsersList.find(u => u.matrixUserId === currentMatrixUserId);
        const currentUserUuid = getUserId(currentUser);

        const employeeUuidsRaw = props.formData?.selectedUsersByType[USER_TYPE_EMPLOYEES] || [];
        const clientUuidsRaw = props.formData?.selectedUsersByType[USER_TYPE_CLIENTS] || [];
        const isCurrentUserEmployee = employeeUuidsRaw.includes(currentUserUuid);
        const currentUserType = isCurrentUserEmployee ? USER_TYPE_EMPLOYEES : USER_TYPE_CLIENTS;
        const currentUserGroup = currentUserUuid ? [currentUserUuid] : [];

        const otherEmployees = employeeUuidsRaw.filter(u => u !== currentUserUuid);
        const otherClients = clientUuidsRaw.filter(u => u !== currentUserUuid);

        const renderGroup = (uuids, label, userType) => {
            if (!uuids.length) return null;

            return (
                <>
                    {label && (
                        <Text align="left" color="#4B5155" weight="700" size="0.9rem" margin="0.5rem">
                            {label}
                        </Text>
                    )}
                    {uuids.map(uuid => {
                        const user = allUsersMap.get(uuid);
                        if (!user) return null;

                        const isCurrentUser = user.matrixUserId === currentMatrixUserId;
                        const couldOwnThisChat = eligibleNewAdminUuids.has(uuid);
                        const userFullName = trimStringWithEllipsis(capitalizeString(user.firstName) + " " + capitalizeString(user.lastName));

                        return (
                            <ListItem key={uuid}>
                                <FlexContainer direction="row" alignItems="center">
                                    <UserAvatar
                                        width="3.125rem"
                                        height="3.125rem"
                                        hideInfo={true}
                                        photo={user.profilePictureUrl}
                                        background="#D6D6D6"
                                        firstName={user.firstName}
                                        lastName={user.lastName}
                                        statusBorder="1px solid #fff"
                                        hideStatus={true}
                                        withShadow={true}
                                        pixelBorder="1px"
                                        showPresenceStatus={true}
                                        isOnline={isUserOnline(matrixClient, user.matrixUserId)}
                                        onClickUserAvatarFunction={() => displayOtherUserProfileModal(user)}
                                    />
                                    <Text margin="0" color="#7B8793" size="1rem" weight="500">
                                        {userFullName}
                                    </Text>
                                </FlexContainer>

                                <MemberActions>
                                    {pendingRemovalUuids.has(uuid) && (
                                        <PendingRemovalBadge>Pending removal</PendingRemovalBadge>
                                    )}
                                    {props.isSelectingNewAdmin && !isCurrentUser && (
                                        <Tooltip
                                            arrow
                                            placement="top"
                                            title={couldOwnThisChat
                                                ? ""
                                                : "This user doesn't have the required permissions to own this chat."}
                                        >
                                            <AdminRadioTooltipTarget>
                                                <AdminRadioButton
                                                    type="button"
                                                    role="radio"
                                                    aria-label={`Select ${userFullName} as new group admin`}
                                                    aria-checked={props.selectedNewAdminUuid === uuid}
                                                    disabled={!couldOwnThisChat}
                                                    $selected={props.selectedNewAdminUuid === uuid}
                                                    onClick={() => props.onSelectNewAdmin(uuid)}
                                                />
                                            </AdminRadioTooltipTarget>
                                        </Tooltip>
                                    )}
                                    {!props.isSelectingNewAdmin && !isCurrentUser && (
                                        <AddOrRemoveIcon
                                            src={RemoveIcon}
                                            alt="remove"
                                            onClick={() => handleUserSelection(getUserId(user), userType)}
                                        />
                                    )}
                                </MemberActions>
                            </ListItem>
                        );
                    })}
                </>
            );
        };

        return (
            <>
                {renderGroup(currentUserGroup, "", currentUserType)}
                {otherEmployees.length > 0 && renderGroup(otherEmployees, "Employees", USER_TYPE_EMPLOYEES)}
                {otherClients.length > 0 && renderGroup(otherClients, "Clients", USER_TYPE_CLIENTS)}
            </>
        );
    };

    return (
        <React.Fragment>
            <Text align="left" color="#4B5155" weight="700" size="1rem" margin="1rem 0 0.8125rem 0">
                {!hasLeadershipRoles && "Group Name (Optional)"}
            </Text>
            <StyledInput
                value={chatName}
                onChange={e => setChatName(e.target.value)}
                placeholder="Create a group name (optional)"
            />
            <StandardModalInfoCard style={{ marginBottom: "1.5rem" }}>
                <SettingRow>
                    <div>
                        <Text align="left" color={portalColors.text} weight="700" size="0.9375rem" margin="0">
                            Allow users to leave chat
                        </Text>
                        <StandardModalHelperText>
                            When off, members must request removal from the group admin.
                        </StandardModalHelperText>
                    </div>
                    <ToggleSwitch
                        checked={props.formData.allowUsersToLeave}
                        name="allowUsersToLeave"
                        onChange={allowUsersToLeave => props.setFormData(prev => ({ ...prev, allowUsersToLeave }))}
                    />
                </SettingRow>
            </StandardModalInfoCard>
            <FlexRow>
                <Container margin="0 1rem 0 0">
                    <Upper>
                        <Text align="left" color="#4B5155" weight="700" size="1rem" margin="0 0 0.8125rem 0">
                            Select {isEmployee ? `${capitalizeString(pluralize.singular(customNames.employees))}` : `${capitalizeString(pluralize.singular(customNames.clients))}`}
                        </Text>
                        <SearchInput
                            onChange={(e) => handleSearch(e.target.value)}
                            placeholder="Search People"
                            margin="0"
                        />
                    </Upper>
                    <Card>{renderUnselectedUsersList()}</Card>
                </Container>
                <Container margin="0 0 0 0">
                    <Upper>
                        <Text margin="0 0 0.8125rem 0" size="1rem">&nbsp;</Text>
                        <Text
                            align="left"
                            color="#4B5155"
                            size="1rem"
                            weight="700"
                            margin="0"
                            height="2.5rem"
                            display="flex"
                            flexDirection="column"
                            justifyContent="center"
                        >
                            {formType === FORM_TYPE_CREATE ? "" : "Edit"} Group Members
                        </Text>
                    </Upper>
                    <Card>{renderSelectedUsersList()}</Card>
                </Container>
            </FlexRow>
        </React.Fragment>
    );
};

export default Group;
